import * as ko from "knockout";
import * as moment from "moment";
import * as localisationHelper from "localisationHelper";

// Types
import Moment = moment.Moment;

// Enums
import { DateTimeWorkflowStates } from "Content/ts/Shared/Controls/DateTime/Services/workflowStates";

// Models
import { DateTimeValidationConfiguration } from "Content/ts/Models/Shared/DateTime/dateTimePickerConfiguration";

// Services
import { DateTimeFormatterService } from "Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService";

export const Deps = {
    LocalisationHelper: localisationHelper
}

export namespace DateTimePickerValidationServices {
    export function ConfigureValidationRules(validationConfiguration: DateTimeValidationConfiguration): Array<any> {
        return [
            {
                validator: (value?: Moment) => {
                    return HasDateAndTimeBeenSet(value, validationConfiguration);
                },
                message: Deps.LocalisationHelper.getString("DateTimeResources", "EnsureDateTimeIsProvided")
            },
            {
                validator: (value?: Moment) => {
                    return HasDateBeenSet(value, validationConfiguration);
                },
                message: Deps.LocalisationHelper.getString("DateTimeResources", "EnsureDateIsProvided")
            },
            {
                validator: (value?: Moment) => {
                    return HasTimeBeenSet(value, validationConfiguration);
                },
                message: Deps.LocalisationHelper.getString("DateTimeResources", "EnsureTimeIsProvided")
            },
            {
                validator: (value?: Moment) => {
                    return IsDateTimeSelectionValid(value, validationConfiguration);
                },
                message: (validationDateTime: Moment | KnockoutObservable<Moment>): string => {
                    if (!validationDateTime) {
                        return null;
                    }

                    const unwrappedDateTime = ko.unwrap(validationDateTime);

                    return Deps.LocalisationHelper.getString("DateTimeResources", "DateTimeInvalidMessage")
                        .replace("{0}", unwrappedDateTime.format(DateTimeFormatterService.LocalisedMomentLongDateFormat));
                },
                params: validationConfiguration.ValidationDateTime
            },
            {
                validator: (value?: Moment) => {
                    return IsValidMinute(value, validationConfiguration);
                },
                message: () => {
                    if (!validationConfiguration.AllowedMinutes) {
                        return "";
                    }
                    
                    if (!validationConfiguration.AllowedMinutesValidationMessage) {
                        return Deps.LocalisationHelper.getString("DateTimeResources", "MinuteValueNotAllowed")
                            .replace("{0}", validationConfiguration.AllowedMinutes.join(", "));
                    }

                    return validationConfiguration.AllowedMinutesValidationMessage
                }
            }
        ];
    }

    function IsDateTimeSelectionValid(value: string | Moment, validationConfiguration: DateTimeValidationConfiguration): boolean {
        // Purpose      :   If no date/time has been provided to validate against then return true and carry on as normal
        if (!value || !ko.unwrap(validationConfiguration.ValidationDateTime)) {
            return true;
        }

        // Purpose      :   If the 'value' is before the 'ValidationDateTime' property then return false. Where the 'value' is 
        //                  equal to or before the 'ValidationDateTime' property return true.
        const validationDateTime = ko.unwrap(validationConfiguration.ValidationDateTime);
        if (validationDateTime) {            
            let parsedDateTime = value;

            // Purpose  :   There are occasions where the passed in value is in a string format not a date. This validates the
            //              configuration is in the correct format and if not will enforce that it is changed accordingly.
            if (!moment.isMoment(parsedDateTime)) {
                parsedDateTime = moment(value);
            }

            return parsedDateTime.isSameOrBefore(validationDateTime);
        }

        return true;
    };

    function HasDateAndTimeBeenSet(value: Moment, validationConfiguration: DateTimeValidationConfiguration): boolean {
        if (!ko.unwrap(validationConfiguration.IsDateRequired) && !ko.unwrap(validationConfiguration.IsTimeRequired)) {
            return true;
        }

        if (validationConfiguration.ComponentState != DateTimeWorkflowStates.DateTimeMode) {
            return true;
        }

        return value != null && value != undefined && validationConfiguration.HasSetDateProperty() && validationConfiguration.HasSetTimeProperty();
    };

    function HasDateBeenSet(value: Moment, validationConfiguration: DateTimeValidationConfiguration): boolean {
        if (!ko.unwrap(validationConfiguration.IsDateRequired) || validationConfiguration.ComponentState != DateTimeWorkflowStates.DateMode) {
            return true;
        }

        return value != null && value != undefined && validationConfiguration.HasSetDateProperty();
    };

    function HasTimeBeenSet(value: Moment, validationConfiguration: DateTimeValidationConfiguration): boolean {
        if (!ko.unwrap(validationConfiguration.IsTimeRequired) || validationConfiguration.ComponentState != DateTimeWorkflowStates.TimeMode) {
            return true;
        }

        return value != null && value != undefined && validationConfiguration.HasSetTimeProperty();
    };

    function IsValidMinute(value: Moment, validationConfiguration: DateTimeValidationConfiguration) : boolean {
        const allowedMinutes = ko.unwrap(validationConfiguration.AllowedMinutes);

        if (!allowedMinutes) {
            return true;
        }

        return allowedMinutes.indexOf(value.minute()) >= 0;
    }
}
